import { useCallback, useEffect, useRef, useState } from 'react';

import { ChantBed } from '../lib/chant.js';

/**
 * The music bed, wrapped for React.
 *
 * All the sound lives in `lib/chant.js` — a generative plainchant rather than
 * the sustained drone this used to be. This hook is only the lifecycle: create
 * on first gesture (autoplay policy allows nothing earlier), pause when the tab
 * is hidden, and release on unmount.
 */
export function useAmbientAudio(initiallyOn = false) {
  const [enabled, setEnabled] = useState(false);
  const [supported, setSupported] = useState(true);
  const [ducked, setDucked] = useState(false);
  const bedRef = useRef(null);

  const bed = useCallback(() => {
    if (!bedRef.current) bedRef.current = new ChantBed();
    return bedRef.current;
  }, []);

  useEffect(() => {
    const AudioCtx = window.AudioContext ?? window.webkitAudioContext;
    if (!AudioCtx) setSupported(false);
  }, []);

  const start = useCallback(async () => {
    const started = await bed().start();
    if (!started) {
      setSupported(false);
      return false;
    }
    setEnabled(true);
    return true;
  }, [bed]);

  const stop = useCallback(() => {
    bedRef.current?.stop();
    setEnabled(false);
  }, []);

  const toggle = useCallback(() => {
    if (enabled) stop();
    else start();
  }, [enabled, start, stop]);

  /** Pull the chant back under narration. Safe to call when it's off. */
  const duck = useCallback(
    (on) => {
      setDucked(!!on);
      bed().duck(on);
    },
    [bed],
  );

  const setLevel = useCallback(
    (value) => {
      bed().setLevel(value);
    },
    [bed],
  );

  // Nobody wants chant from a background tab.
  useEffect(() => {
    if (!enabled) return undefined;
    const onVisibility = () => {
      if (document.hidden) bedRef.current?.suspend();
      else bedRef.current?.resume();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enabled]);

  useEffect(
    () => () => {
      bedRef.current?.dispose();
      bedRef.current = null;
    },
    [],
  );

  // `initiallyOn` is remembered from a previous session, but autoplay policy
  // means it can't be honoured without a gesture — it's surfaced so the button
  // can render as "armed" and start on the first tap.
  return { enabled, supported, ducked, duck, setLevel, toggle, start, stop, wasOn: initiallyOn };
}
